<template>
    <div class="file-uploader-modern">
        <!-- Header (opcional) -->
        <div v-if="showHeader" class="uploader-header">
            <div class="header-left">
                <i class="fas fa-cloud-upload-alt"></i>
                <h3>{{ title }}</h3>
            </div>
            <div class="header-right">
                <span v-if="patientMedicalNoteId" class="badge badge-consultation">
                    <i class="fas fa-notes-medical"></i>
                    Consulta #{{ patientMedicalNoteId }}
                </span>
                <span v-else class="badge badge-general">
                    <i class="fas fa-folder"></i>
                    ARCHIVOS GENERALES
                </span>
            </div>
        </div>

        <!-- Badge de tipo (cuando no hay header) -->
        <div v-if="patientMedicalNoteId" class="badge-container">
            <span class="badge badge-consultation">
                <i class="fas fa-notes-medical"></i>
                Consulta #{{ patientMedicalNoteId }}
            </span>
        </div>

        <!-- Drag & Drop Zone -->
        <div
class="drop-zone" :class="{ 'drag-over': isDragOver, 'has-files': files.length > 0 }"
            @dragenter.prevent="onDragEnter" @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop">
            <input
ref="fileInput" type="file" multiple :accept="acceptedFormats" class="file-input-hidden"
                @change="onFileSelect" />

            <div v-if="files.length === 0" class="drop-zone-empty">
                <div class="upload-icon">
                    <i class="fas fa-cloud-upload-alt fa-3x"></i>
                </div>
                <h4>Arrastra archivos aquí</h4>
                <p>o haz clic para seleccionar</p>
                <button class="btn-browse" @click="browseFiles">
                    <i class="fas fa-folder-open"></i>
                    Seleccionar Archivos
                </button>
                <div class="supported-formats">
                    <small>Formatos: PDF, Word, Excel, Imágenes (máx. 10MB)</small>
                </div>
            </div>

            <div v-else class="files-preview">
                <div class="files-header">
                    <h5>
                        <i class="fas fa-file-alt"></i>
                        {{ files.length }} archivo{{ files.length > 1 ? 's' : '' }} seleccionado{{ files.length > 1 ?
                            's' : '' }}
                    </h5>
                    <button class="btn-add-more" @click="browseFiles">
                        <i class="fas fa-plus"></i>
                        Agregar más
                    </button>
                </div>

                <div class="files-list">
                    <div
v-for="(file, index) in files" :key="index" class="file-item"
                        :class="{ 'uploading': file.uploading, 'error': file.error, 'success': file.success }">
                        <div class="file-icon">
                            <i :class="getFileIcon(file)"></i>
                        </div>
                        <div class="file-info">
                            <div class="file-name">{{ file.name }}</div>
                            <div class="file-meta">
                                <span class="file-size">{{ formatFileSize(file.size) }}</span>
                                <span v-if="file.progress !== undefined" class="file-progress">
                                    {{ file.progress }}%
                                </span>
                            </div>

                            <!-- Tipo de archivo -->
                            <div class="file-type-selector">
                                <select
v-model="file.fileType" class="form-select form-select-sm"
                                    :disabled="file.uploading || file.success">
                                    <option value="">Tipo de archivo...</option>
                                    <option value="laboratorio">Laboratorio</option>
                                    <option value="estudio">Estudio Médico</option>
                                    <option value="imagen">Imagen</option>
                                    <option value="radiografia">Radiografía</option>
                                    <option value="tomografia">Tomografía</option>
                                    <option value="resonancia">Resonancia</option>
                                    <option value="ultrasonido">Ultrasonido</option>
                                    <option value="electrocardiograma">Electrocardiograma</option>
                                    <option value="documento">Documento</option>
                                    <option value="receta">Receta</option>
                                    <option value="consentimiento">Consentimiento</option>
                                    <option value="referencia">Referencia</option>
                                    <option value="otro">Otro</option>
                                </select>
                            </div>

                            <!-- Descripción -->
                            <textarea
v-model="file.description" placeholder="Descripción (opcional)..."
                                class="form-control form-control-sm mt-2" rows="2"
                                :disabled="file.uploading || file.success"></textarea>

                            <!-- Fecha del archivo -->
                            <div class="file-date-selector mt-2">
                                <input
v-model="file.fileDate" type="date" class="form-control form-control-sm"
                                    :disabled="file.uploading || file.success" placeholder="Fecha del archivo" />
                            </div>

                            <!-- Opciones adicionales -->
                            <div class="file-options mt-2">
                                <label class="checkbox-label">
                                    <input
v-model="file.favorite" type="checkbox"
                                        :disabled="file.uploading || file.success" />
                                    <i class="fas fa-star"></i>
                                    Favorito
                                </label>
                                <label class="checkbox-label">
                                    <input
v-model="file.shared" type="checkbox"
                                        :disabled="file.uploading || file.success" />
                                    <i class="fas fa-share-alt"></i>
                                    Compartir
                                </label>
                            </div>

                            <!-- Vista previa de imagen -->
                            <div v-if="isImage(file) && file.preview" class="image-preview mt-2">
                                <img :src="file.preview" :alt="file.name" />
                            </div>

                            <!-- Vista previa después de subir (con signed_url) -->
                            <div
v-if="file.success && file.uploadedData?.signed_url && isImageByMime(file.uploadedData)"
                                class="image-preview-uploaded mt-2">
                                <img :src="file.uploadedData.signed_url" :alt="file.name" />
                                <div class="preview-label">
                                    <i class="fas fa-check-circle"></i>
                                    Vista previa segura
                                </div>
                            </div>

                            <!-- Progress bar -->
                            <div v-if="file.uploading" class="progress-bar-container">
                                <div class="progress-bar" :style="{ width: file.progress + '%' }"></div>
                            </div>

                            <!-- Status messages -->
                            <div v-if="file.error" class="file-error">
                                <i class="fas fa-exclamation-circle"></i>
                                {{ file.errorMessage }}
                            </div>
                            <div v-if="file.success" class="file-success">
                                <i class="fas fa-check-circle"></i>
                                Subido exitosamente
                            </div>
                        </div>
                        <button v-if="!file.uploading && !file.success" class="btn-remove" @click="removeFile(index)">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Upload Button -->
        <div v-if="files.length > 0 && uploadSummary.total === 0" class="upload-actions">
            <button class="btn-upload" :disabled="isUploading || !canUpload" @click="uploadFiles">
                <i class="fas" :class="isUploading ? 'fa-spinner fa-spin' : 'fa-cloud-upload-alt'"></i>
                {{ isUploading ? 'Subiendo...' : 'Subir Archivos' }}
            </button>
        </div>

        <!-- Upload Summary -->
        <div v-if="uploadSummary.total > 0" class="upload-summary">
            <div class="summary-stats">
                <div class="stat-item success">
                    <i class="fas fa-check-circle"></i>
                    <span>{{ uploadSummary.success }} exitosos</span>
                </div>
                <div v-if="uploadSummary.failed > 0" class="stat-item error">
                    <i class="fas fa-times-circle"></i>
                    <span>{{ uploadSummary.failed }} fallidos</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import api from '@/services/api';

const { proxy } = getCurrentInstance();

const props = defineProps({
    patientId: {
        type: [Number, String],
        required: true
    },
    patientMedicalNoteId: {
        type: [Number, String],
        default: null
    },
    title: {
        type: String,
        default: 'Subir Archivos'
    },
    autoUpload: {
        type: Boolean,
        default: false
    },
    showHeader: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['uploaded', 'error', 'complete']);

// Refs
const fileInput = ref(null);
const files = ref([]);
const isDragOver = ref(false);
const isUploading = ref(false);
const uploadSummary = ref({
    total: 0,
    success: 0,
    failed: 0
});

// Formatos aceptados
const acceptedFormats = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.bmp,.tiff';

// Computed
const canUpload = computed(() => {
    return files.value.length > 0 && !isUploading.value;
});

// Methods
const browseFiles = () => {
    fileInput.value.click();
};

const onFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    addFiles(selectedFiles);
};

const onDragEnter = () => {
    isDragOver.value = true;
};

const onDragOver = () => {
    isDragOver.value = true;
};

const onDragLeave = () => {
    isDragOver.value = false;
};

const onDrop = (event) => {
    isDragOver.value = false;
    const droppedFiles = Array.from(event.dataTransfer.files);
    addFiles(droppedFiles);
};

const addFiles = (newFiles) => {
    newFiles.forEach(file => {
        // Validar tamaño (10MB)
        if (file.size > 10 * 1024 * 1024) {
            alert(`El archivo ${file.name} excede el tamaño máximo de 10MB`);
            return;
        }

        const fileItem = {
            file: file,
            name: file.name,
            size: file.size,
            fileType: '',
            description: '',
            fileDate: new Date().toISOString().split('T')[0], // Fecha actual por defecto
            favorite: false,
            shared: false,
            preview: null, // Para preview local
            uploadedData: null, // Para datos del archivo subido (con signed_url)
            uploading: false,
            progress: 0,
            success: false,
            error: false,
            errorMessage: ''
        };

        // Generar preview local para imágenes
        if (isImageFile(file)) {
            const reader = new FileReader();
            reader.onload = (e) => {
                fileItem.preview = e.target.result;
            };
            reader.readAsDataURL(file);
        }

        files.value.push(fileItem);
    });

    if (props.autoUpload && newFiles.length > 0) {
        uploadFiles();
    }
};

const removeFile = (index) => {
    files.value.splice(index, 1);
};

const clearAll = () => {
    files.value = files.value.filter(f => f.uploading || f.success);
};

const uploadFiles = async () => {
    isUploading.value = true;
    uploadSummary.value = { total: files.value.length, success: 0, failed: 0 };

    for (const fileItem of files.value) {
        if (fileItem.success || fileItem.uploading) continue;

        fileItem.uploading = true;
        fileItem.progress = 0;

        try {
            const formData = new FormData();
            formData.append('patient_id', props.patientId);
            formData.append('type', 'file');
            formData.append('file', fileItem.file);

            if (props.patientMedicalNoteId) {
                formData.append('patient_medical_note_id', props.patientMedicalNoteId);
            }

            if (fileItem.fileType) {
                formData.append('file_type', fileItem.fileType);
            }

            if (fileItem.description) {
                formData.append('description', fileItem.description);
            }

            if (fileItem.fileDate) {
                formData.append('file_date', fileItem.fileDate);
            }

            if (fileItem.favorite) {
                formData.append('favorite', '1');
            }

            if (fileItem.shared) {
                formData.append('shared', '1');
            }

            const response = await api.post('/patient-files/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    fileItem.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                }
            });

            fileItem.uploading = false;
            fileItem.success = true;
            fileItem.progress = 100;

            // Guardar los datos del archivo subido (incluye signed_url)
            fileItem.uploadedData = response.data.data;

            uploadSummary.value.success++;

            emit('uploaded', response.data);

        } catch (error) {
            fileItem.uploading = false;
            fileItem.error = true;
            fileItem.errorMessage = error.response?.data?.message || 'Error al subir el archivo';
            uploadSummary.value.failed++;

            emit('error', { file: fileItem, error });
        }
    }

    isUploading.value = false;
    emit('complete', uploadSummary.value);

    // Mostrar solo notificación de errores si hay
    if (uploadSummary.value.failed > 0) {
        const failMsg = uploadSummary.value.failed === 1
            ? '1 archivo falló'
            : `${uploadSummary.value.failed} archivos fallaron`;

        proxy.$notify(
            'Error al subir',
            failMsg,
            'danger',
            'fas fa-exclamation-triangle'
        );
    }
};

const getFileIcon = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    const iconMap = {
        pdf: 'fas fa-file-pdf text-danger',
        doc: 'fas fa-file-word text-primary',
        docx: 'fas fa-file-word text-primary',
        xls: 'fas fa-file-excel text-success',
        xlsx: 'fas fa-file-excel text-success',
        csv: 'fas fa-file-csv text-success',
        jpg: 'fas fa-file-image text-info',
        jpeg: 'fas fa-file-image text-info',
        png: 'fas fa-file-image text-info',
        gif: 'fas fa-file-image text-info'
    };
    return iconMap[ext] || 'fas fa-file text-secondary';
};

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// Helper para verificar si un archivo es imagen por su File object
const isImageFile = (file) => {
    return file.type && file.type.startsWith('image/');
};

// Helper para verificar si un archivo es imagen (para el componente)
const isImage = (fileItem) => {
    if (fileItem.file) {
        return isImageFile(fileItem.file);
    }
    const ext = fileItem.name.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'].includes(ext);
};

// Helper para verificar si un archivo subido es imagen por mime_type
const isImageByMime = (uploadedData) => {
    return uploadedData.mime_type && uploadedData.mime_type.startsWith('image/');
};

// Watch for auto-upload
watch(() => props.autoUpload, (newVal) => {
    if (newVal && files.value.length > 0) {
        uploadFiles();
    }
});

// Exponer métodos para uso externo
defineExpose({
    uploadFiles,
    clearAll,
    files,
    isUploading,
    canUpload
});
</script>

<style scoped>
.file-uploader-modern {
    background: transparent;
    border-radius: 0;
    box-shadow: none;
    overflow: visible;
}

.uploader-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

.badge-container {
    display: flex;
    justify-content: center;
    padding: 0 0 1.5rem 0;
    margin-top: -1rem;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.header-left i {
    font-size: 1.5rem;
}

.header-left h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
}

.header-right .badge,
.badge-container .badge {
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.badge-consultation {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.badge-general {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.drop-zone {
    padding: 2rem;
    min-height: 300px;
    border: 3px dashed #d1d5db;
    border-radius: 8px;
    margin: 1.5rem;
    transition: all 0.3s ease;
    cursor: pointer;
}

.drop-zone:hover {
    border-color: #667eea;
    background: #f9fafb;
}

.drop-zone.drag-over {
    border-color: #667eea;
    background: #ede9fe;
    transform: scale(1.01);
}

.file-input-hidden {
    display: none;
}

.drop-zone-empty {
    text-align: center;
    padding: 2rem;
}

.upload-icon {
    color: #667eea;
    margin-bottom: 1.5rem;
    animation: float 3s ease-in-out infinite;
}

@keyframes float {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-10px);
    }
}

.drop-zone-empty h4 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0.5rem;
}

.drop-zone-empty p {
    color: #6b7280;
    margin-bottom: 1.5rem;
}

.btn-browse {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    padding: 0.75rem 2rem;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: transform 0.2s ease;
}

.btn-browse:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.supported-formats {
    margin-top: 1rem;
    color: #9ca3af;
}

.files-preview {
    padding: 1rem 0;
}

.files-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
}

.files-header h5 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
}

.btn-add-more {
    background: #f3f4f6;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    color: #667eea;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-add-more:hover {
    background: #e5e7eb;
}

.files-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.file-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
    background: #f9fafb;
    border-radius: 8px;
    border: 2px solid transparent;
    transition: all 0.3s ease;
}

.file-item:hover {
    background: #f3f4f6;
}

.file-item.uploading {
    border-color: #667eea;
    background: #ede9fe;
}

.file-item.success {
    border-color: #10b981;
    background: #d1fae5;
}

.file-item.error {
    border-color: #ef4444;
    background: #fee2e2;
}

.file-icon {
    font-size: 2rem;
    flex-shrink: 0;
}

.file-info {
    flex-grow: 1;
}

.file-name {
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0.25rem;
}

.file-meta {
    display: flex;
    gap: 1rem;
    font-size: 0.875rem;
    color: #6b7280;
    margin-bottom: 0.5rem;
}

.file-type-selector {
    margin: 0.5rem 0;
}

.file-date-selector {
    width: 100%;
}

.file-date-selector input {
    width: 100%;
}

.file-options {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #4b5563;
    cursor: pointer;
    user-select: none;
}

.checkbox-label input[type="checkbox"] {
    cursor: pointer;
}

.checkbox-label:hover {
    color: #667eea;
}

.checkbox-label i {
    color: #9ca3af;
    transition: color 0.2s;
}

.checkbox-label input:checked+i {
    color: #667eea;
}

.image-preview,
.image-preview-uploaded {
    margin-top: 0.75rem;
    border-radius: 8px;
    overflow: hidden;
    border: 2px solid #e5e7eb;
    position: relative;
}

.image-preview img,
.image-preview-uploaded img {
    width: 100%;
    max-height: 200px;
    object-fit: contain;
    background: #f9fafb;
    display: block;
}

.image-preview-uploaded {
    border-color: #10b981;
}

.preview-label {
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(16, 185, 129, 0.95);
    color: white;
    padding: 0.375rem 0.75rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.375rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.progress-bar-container {
    width: 100%;
    height: 6px;
    background: #e5e7eb;
    border-radius: 3px;
    overflow: hidden;
    margin-top: 0.75rem;
}

.progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    transition: width 0.3s ease;
}

.file-error,
.file-success {
    margin-top: 0.5rem;
    padding: 0.5rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
}

.file-error {
    background: #fee2e2;
    color: #dc2626;
}

.file-success {
    background: #d1fae5;
    color: #059669;
}

.btn-remove {
    background: #ef4444;
    border: none;
    color: white;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.2s ease;
}

.btn-remove:hover {
    background: #dc2626;
    transform: scale(1.1);
}

.uploader-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding: 1.5rem;
    background: #f9fafb;
    border-top: 1px solid #e5e7eb;
}

.btn {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-secondary {
    background: #6b7280;
    color: white;
}

.btn-secondary:hover:not(:disabled) {
    background: #4b5563;
}

.btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.upload-actions {
    padding: 1.5rem;
    display: flex;
    justify-content: center;
}

.btn-upload {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    padding: 1rem 3rem;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-upload:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.btn-upload:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.btn-upload i {
    font-size: 1.1rem;
}

.upload-summary {
    padding: 1rem 0;
    background: #f9fafb;
    border-top: 1px solid #e5e7eb;
}

.summary-stats {
    display: flex;
    gap: 2rem;
    justify-content: center;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
}

.stat-item.success {
    color: #059669;
}

.stat-item.error {
    color: #dc2626;
}

.stat-item i {
    font-size: 1.25rem;
}
</style>
